/**
 * Connect settle, playout-trend dwell, cut stepping and floor reclaim: the constants and the
 * one pure decision the recovery controller was missing.
 *
 * Field shape (Android, 2026-09-19): the controller cut the bitrate cap 77% on the first or
 * second guest sample after connect - while glass-to-glass was at its best of the run - and
 * fired `elevated` on single ticks of a signal that swung 213 -> 41 -> 181 -> 20 ms. Startup
 * is not congestion, a single tick is not a trend, and a floor raised for cause must be
 * releasable once measurement proves the cause is gone (PRODUCT DECISION D1, amended).
 *
 * Browser-free and Node/Jest-safe.
 */

import {
  DEFAULT_STANDING_LATENCY_THRESHOLDS,
  type LatencyFloorKind,
} from './standingLatency/standingLatencyContracts';

/**
 * No cut, floor raise, fps ceiling or tier change before this much time has passed since a
 * peer's first `clientStats`. Aligned with PLAYOUT_LATENCY_PINNED_WARMUP_MS so the trend's
 * baseline is trusted by the time anything may act on it.
 */
export const CONNECT_SETTLE_MS = 10_000;

/** `playout-trend` must exceed its threshold continuously this long before it may decide a verdict. */
export const PLAYOUT_TREND_DWELL_MS = 3_000;
/** After firing, the trend must fall this far below the threshold before it may fire again. */
export const PLAYOUT_TREND_HYSTERESIS_MS = 50;

/**
 * An `elevated` verdict steps the cap to this fraction of its current value (bounded below
 * by the recovery cap), instead of dropping straight to the cap.
 */
export const ELEVATED_CUT_STEP_RATIO = 0.75;

/** Continuous within-budget time before a sticky floor may be released one rung. */
export const RECLAIM_WITHIN_BUDGET_MS = 15_000;

export type ReclaimEvidence = {
  /** Continuous ms during which measured glass-to-glass stayed within budget. */
  withinBudgetForMs: number;
  /** Latest standing-latency verdict kind for the peer. */
  standingLatencyKind: LatencyFloorKind;
  /** Highest loss percent seen during the evidence window. */
  lossPctMax: number;
  /** Highest standing queue seen during the evidence window, ms. */
  standingQueueMsMax: number;
};

/**
 * Whether the evidence justifies releasing one rung of a sticky floor. Pure.
 *
 * A standing buffer is disqualifying even when glass-to-glass reads within budget: releasing a
 * floor (raising fps or resolution) while frames are standing risks re-inflating it.
 */
export function canReclaim(
  evidence: ReclaimEvidence,
  options: { withinBudgetMs?: number; maxLossPct?: number; maxQueueMs?: number } = {},
): boolean {
  const withinBudgetMs = options.withinBudgetMs ?? RECLAIM_WITHIN_BUDGET_MS;
  const maxLossPct = options.maxLossPct ?? DEFAULT_STANDING_LATENCY_THRESHOLDS.maxLossPct;
  const maxQueueMs = options.maxQueueMs ?? DEFAULT_STANDING_LATENCY_THRESHOLDS.maxQueueMs;
  if (!Number.isFinite(evidence.withinBudgetForMs) || evidence.withinBudgetForMs < withinBudgetMs) return false;
  if (evidence.standingLatencyKind === 'standing-buffer') return false;
  if (!Number.isFinite(evidence.lossPctMax) || evidence.lossPctMax > maxLossPct) return false;
  if (!Number.isFinite(evidence.standingQueueMsMax) || evidence.standingQueueMsMax > maxQueueMs) return false;
  return true;
}
